import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Building2, ShieldCheck, Lock, Send, Loader2, Check,
  Mail, Phone, Clock, MapPin, User, Hash, Info, AlertCircle
} from 'lucide-react';
import { Button, Eyebrow, Reveal, Section, PageBanner } from '../components/ui';

function maskCNPJ(v) {
  const d = v.replace(/\D/g, '').slice(0, 14);
  if (d.length <= 2) return d;
  if (d.length <= 5) return d.slice(0, 2) + '.' + d.slice(2);
  if (d.length <= 8) return d.slice(0, 2) + '.' + d.slice(2, 5) + '.' + d.slice(5);
  if (d.length <= 12) return d.slice(0, 2) + '.' + d.slice(2, 5) + '.' + d.slice(5, 8) + '/' + d.slice(8);
  return d.slice(0, 2) + '.' + d.slice(2, 5) + '.' + d.slice(5, 8) + '/' + d.slice(8, 12) + '-' + d.slice(12);
}

function maskPhone(v) {
  const d = v.replace(/\D/g, '').slice(0, 11);
  if (d.length < 3) return d;
  if (d.length < 7) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length < 11) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
}

function Field({ id, label, value, onChange, onBlur, error, type = 'text', placeholder, Icon }) {
  return (
    <div>
      <label htmlFor={id} className="field-label">{label}</label>
      <div className="relative">
        {Icon && <span className="absolute left-4 top-1/2 -translate-y-1/2 text-verde-medio"><Icon size={18} /></span>}
        <input id={id} type={type} value={value} onChange={onChange} onBlur={onBlur} placeholder={placeholder}
          className={`field ${Icon ? 'pl-12' : ''} ${error ? 'invalid' : ''}`} />
      </div>
      {error && <div className="text-[0.82rem] mt-1.5 text-[#b94a48] flex items-center gap-1.5"><AlertCircle size={14} />{error}</div>}
    </div>
  );
}

export default function Contato() {
  const [form, setForm] = useState({ nome: '', cnpj: '', email: '', telefone: '' });
  const [touched, setTouched] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const errors = {
    nome: form.nome.trim().split(/\s+/).filter(Boolean).length < 2 ? 'Informe nome e sobrenome' : '',
    cnpj: form.cnpj.replace(/\D/g, '').length !== 14 ? 'CNPJ deve ter 14 dígitos' : '',
    email: !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email) ? 'E-mail corporativo inválido' : '',
    telefone: (() => { const d = form.telefone.replace(/\D/g, ''); return d.length !== 10 && d.length !== 11 ? 'Telefone inválido' : ''; })(),
  };
  const hasErrors = Object.values(errors).some(Boolean);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const blur = (k) => setTouched((t) => ({ ...t, [k]: true }));

  const submit = (e) => {
    e.preventDefault();
    setTouched({ nome: true, cnpj: true, email: true, telefone: true });
    if (hasErrors) return;
    setSubmitting(true);
    setTimeout(() => { setSubmitting(false); setSent(true); }, 900);
  };

  return (
    <main className="page-enter">
      <Helmet>
        <title>Cadastre Sua Corretora | MedAliados — Parceria Comercial</title>
        <meta name="description" content="Cadastre sua corretora na plataforma MedAliados e comece a vender planos de saúde MedSênior com suporte dedicado, leads qualificados e comissões acima da média. Exclusivo para CNPJ ativo." />
        <link rel="canonical" href="https://medaliados.com.br/contato" />
        <meta property="og:title" content="Cadastre Sua Corretora | MedAliados — Parceria Comercial" />
        <meta property="og:description" content="Preencha o formulário e nossa equipe entra em contato em até 1 dia útil. Onboarding em 48h após aprovação." />
        <meta property="og:url" content="https://medaliados.com.br/contato" />
      </Helmet>
      <PageBanner
        eyebrow="Contato comercial"
        title="Cadastre sua corretora."
        sub="Preencha os dados abaixo. Nossa equipe entrará em contato para iniciar a jornada da sua corretora na MedAliados."
      />

      <Section bg="bege" className="py-20 md:py-28">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <Reveal>
              <div className="rounded-2xl bg-fundo-branco border border-borda p-7 md:p-10 shadow-soft">
                {!sent ? (
                  <>
                    <div className="flex items-center gap-3 mb-2">
                      <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-verde-escuro text-fundo-branco">
                        <Building2 size={18} />
                      </span>
                      <span className="font-mono text-[11px] uppercase tracking-widest text-verde-medio">Cadastro · exclusivo CNPJ</span>
                    </div>
                    <h2 className="font-display font-bold text-[1.8rem] md:text-[2.2rem] text-verde-escuro">Cadastre Sua Corretora</h2>
                    <p className="text-texto-medio mt-2">Todos os campos são obrigatórios. Os dados são tratados conforme nossa política de privacidade.</p>

                    <form onSubmit={submit} className="mt-8 space-y-5" noValidate>
                      <Field id="nome" label="Nome completo do responsável" Icon={User}
                        value={form.nome} onChange={(e) => set('nome', e.target.value)}
                        onBlur={() => blur('nome')} error={touched.nome ? errors.nome : ''} placeholder="Nome e sobrenome" />

                      <Field id="cnpj" label="CNPJ da corretora" Icon={Hash}
                        value={form.cnpj} onChange={(e) => set('cnpj', maskCNPJ(e.target.value))}
                        onBlur={() => blur('cnpj')} error={touched.cnpj ? errors.cnpj : ''} placeholder="XX.XXX.XXX/XXXX-XX" />

                      <Field id="email" label="E-mail corporativo" Icon={Mail} type="email"
                        value={form.email} onChange={(e) => set('email', e.target.value)}
                        onBlur={() => blur('email')} error={touched.email ? errors.email : ''} placeholder="contato@suacorretora.com.br" />

                      <Field id="telefone" label="Telefone / WhatsApp" Icon={Phone}
                        value={form.telefone} onChange={(e) => set('telefone', maskPhone(e.target.value))}
                        onBlur={() => blur('telefone')} error={touched.telefone ? errors.telefone : ''} placeholder="(11) 99999-9999" />

                      <div className="rounded-lg bg-fundo-bege border border-borda p-4 flex gap-3 items-start">
                        <ShieldCheck size={18} className="text-verde-medio mt-0.5 shrink-0" />
                        <div className="text-[0.88rem] text-texto-medio leading-relaxed">
                          Este cadastro é <b className="text-verde-escuro">exclusivo para corretoras com CNPJ ativo</b>. Ao enviar, você concorda com nossa política de privacidade e autoriza o contato comercial.
                        </div>
                      </div>

                      <div className="pt-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <div className="text-[0.85rem] text-texto-medio flex items-center gap-2">
                          <Lock size={14} /> Conexão segura · dados criptografados
                        </div>
                        <Button size="lg" icon={submitting ? Loader2 : Send} onClick={submit}>
                          {submitting ? 'Enviando...' : 'Enviar Cadastro'}
                        </Button>
                      </div>
                    </form>
                  </>
                ) : (
                  <div className="py-10 text-center">
                    <span className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-verde-medio text-fundo-branco mx-auto">
                      <Check size={32} strokeWidth={3} />
                    </span>
                    <h2 className="font-display font-bold text-[1.8rem] md:text-[2.2rem] text-verde-escuro mt-5">Cadastro recebido</h2>
                    <p className="mt-3 text-texto-medio max-w-md mx-auto leading-relaxed">
                      Obrigado, <b className="text-verde-escuro">{form.nome.split(' ')[0]}</b>. Recebemos os dados da <b className="text-verde-escuro">corretora CNPJ {form.cnpj}</b>. Nossa equipe entrará em contato em até <b>1 dia útil</b> pelo e-mail informado.
                    </p>
                    <div className="mt-8 grid sm:grid-cols-3 gap-3 max-w-lg mx-auto text-left">
                      {[
                        ['1', 'Análise', 'Validação do cadastro e CNPJ'],
                        ['2', 'Reunião', 'Apresentação da plataforma'],
                        ['3', 'Onboarding', 'Acesso em até 48h'],
                      ].map(([n, t, d], i) => (
                        <div key={i} className="rounded-lg bg-fundo-bege border border-borda p-4">
                          <div className="font-mono text-[10px] text-verde-medio">{`PASSO ${n}`}</div>
                          <div className="font-display font-semibold text-verde-escuro mt-1">{t}</div>
                          <div className="text-[0.85rem] text-texto-medio mt-0.5">{d}</div>
                        </div>
                      ))}
                    </div>
                    <div className="mt-8">
                      <Button variant="outline" onClick={() => { setSent(false); setForm({ nome: '', cnpj: '', email: '', telefone: '' }); setTouched({}); }}>
                        Novo cadastro
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-5 space-y-5">
            <Reveal delay={120}>
              <div className="rounded-2xl bg-verde-escuro text-fundo-branco p-7 md:p-8">
                <Eyebrow tone="light">Atendimento comercial</Eyebrow>
                <h3 className="font-display font-bold text-[1.5rem] mt-4">Fale com nosso time</h3>
                <p className="text-fundo-branco/70 mt-3 leading-relaxed">Canais diretos para corretoras parceiras e empresas interessadas em integrar a plataforma.</p>
                <ul className="mt-6 space-y-4">
                  {[
                    { Icon: Mail, label: 'E-mail', value: 'contato@medaliados.com.br' },
                    { Icon: Phone, label: 'Telefone', value: '(11) 3000-0000' },
                    { Icon: Clock, label: 'Atendimento', value: 'Seg a Sex · 9h às 18h' },
                    { Icon: MapPin, label: 'Endereço', value: 'São Paulo, SP' },
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-fundo-branco/10 text-verde-lima shrink-0">
                        <item.Icon size={18} />
                      </span>
                      <div>
                        <div className="text-[11px] font-mono uppercase tracking-widest text-fundo-branco/55">{item.label}</div>
                        <div className="font-medium">{item.value}</div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>

            <Reveal delay={180}>
              <div className="rounded-2xl bg-fundo-branco border border-borda p-6 flex items-start gap-4">
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-verde-escuro/5 text-verde-medio shrink-0">
                  <Info size={18} />
                </span>
                <div>
                  <div className="font-display font-semibold text-verde-escuro">Sou pessoa física, posso me cadastrar?</div>
                  <p className="text-[0.92rem] text-texto-medio mt-1.5 leading-relaxed">
                    Não. A MedAliados é uma plataforma B2B, exclusiva para corretoras com CNPJ ativo. Para contratar um plano MedSênior como pessoa física, procure uma corretora parceira.
                  </p>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </Section>
    </main>
  );
}
